import { DataPlaneResourcePathPrefix } from '../constants/data-plane';
import { StringifiedPattern } from '../constants/patterns';
import { isValidDataPlaneUri, parseDataPlanePath } from '../ids/data-plane';
import { ClientError } from '../models/common';
import { DataPlaneIdTokenSet } from '../models/data-plane';
import { isUndefinedOrWhiteSpace } from '../utilities/string';
import { tryOrDefault } from '../utilities/try-or-default';

/* eslint-disable security/detect-non-literal-regexp */
// Justification: regex is non-literal, but is not constructed from user input.
const environmentDefinitionPathPattern = new RegExp(
    `^/${DataPlaneResourcePathPrefix.Project}/${StringifiedPattern.ResourceName}/${DataPlaneResourcePathPrefix.Catalog}/${StringifiedPattern.ResourceName}/${DataPlaneResourcePathPrefix.EnvironmentDefinition}/${StringifiedPattern.ResourceName}$`,
    'i'
);
/* eslint-enable security/detect-non-literal-regexp */

export const createEnvironmentDefinitionDataPlaneUri = (
    tokens: DataPlaneIdTokenSet<'catalogName' | 'devCenter' | 'environmentDefinitionName' | 'projectName'>
): string => {
    const { catalogName, devCenter, environmentDefinitionName, projectName } = tokens;

    if (isUndefinedOrWhiteSpace(catalogName)) {
        throw new ClientError('Catalog name token cannot be undefined or white space');
    }

    if (isUndefinedOrWhiteSpace(devCenter)) {
        throw new ClientError('Dev center token cannot be undefined or white space');
    }

    if (isUndefinedOrWhiteSpace(environmentDefinitionName)) {
        throw new ClientError('Environment definition name token cannot be undefined or white space');
    }

    if (isUndefinedOrWhiteSpace(projectName)) {
        throw new ClientError('Project name token cannot be undefined or white space');
    }

    const path = `/${[
        DataPlaneResourcePathPrefix.Project,
        projectName.toLowerCase(),
        DataPlaneResourcePathPrefix.Catalog,
        catalogName.toLowerCase(),
        DataPlaneResourcePathPrefix.EnvironmentDefinition,
        environmentDefinitionName.toLowerCase(),
    ].join('/')}`;

    const uri = new URL(path, devCenter);

    return `${uri}`;
};

export const getTokensFromEnvironmentDefinitionDataPlaneUri = (
    uri: string
): DataPlaneIdTokenSet<'catalogName' | 'devCenter' | 'environmentDefinitionName' | 'projectName'> => {
    if (!isValidEnvironmentDefinitionDataPlaneUri(uri)) {
        throw new ClientError('Given URI was not a valid Data Plane URI for an environment definition');
    }

    const { origin, pathname } = new URL(uri);
    const [, projectName, , catalogName, , environmentDefinitionName] = parseDataPlanePath(pathname);

    return {
        catalogName,
        devCenter: origin,
        environmentDefinitionName,
        projectName,
    };
};

export const isValidEnvironmentDefinitionDataPlaneUri = (uri: string): boolean =>
    isValidDataPlaneUri(uri, environmentDefinitionPathPattern);

export const tryCreateEnvironmentDefinitionDataPlaneUri = tryOrDefault(createEnvironmentDefinitionDataPlaneUri);

export const tryGetTokensFromEnvironmentDefinitionDataPlaneUri = tryOrDefault(
    getTokensFromEnvironmentDefinitionDataPlaneUri
);
